import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Stack, Tooltip, Typography } from '@mui/material';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import {
  CompartmentSelector,
  CompartmentSelectorIndex,
  EventRule,
  EventRuleAction,
  EventRuleActionType,
} from '@ekkogmbh/apisdk';
import { CompartmentSelectorPicker } from 'src/CompartmentSelectorManagement/CompartmentSelectorPicker';
import { EventTriggerForm } from './EventTriggerForm';
import { ListForm } from 'src/Common/Components/Forms/ListForm';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { ArrowForward } from '@mui/icons-material';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import useEventTrigger from '../Providers/EventTriggerProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';

const getDefaultAction = (type: EventRuleActionType): EventRuleAction => {
  switch (type) {
    case EventRuleActionType.WEBHOOK:
      return {
        type,
        configuration: {
          url: '',
          authEnabled: '0',
          authType: 'oauth2-client-credentials',
          authUrlAccessToken: '',
          authClientId: '',
          authClientSecret: '',
          authToken: '',
          authPrefix: '',
        },
      };
    case EventRuleActionType.MQTT:
      return {
        type,
        configuration: { topic: '' },
      };
    case EventRuleActionType.CLEAR_EVENT_COMPARTMENT:
      return {
        type,
        configuration: { clearValue: '', fields: '' },
      };
    case EventRuleActionType.EKANBAN_TRIGGER:
      return {
        type,
        configuration: {
          compartmentSelectorName: '',
          compatmentSelectorCoordinate: '',
          inputJsonPath: '',
          triggerKey: '',
        },
      };
  }
};

const defaultAction: EventRuleAction = {
  type: EventRuleActionType.WEBHOOK,
  configuration: { url: '' },
};

interface Props {
  eventRule?: EventRule;
  closeHandler: () => void;
}

const Component = ({ eventRule, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { eventTrigger, setTriggers } = useEventTrigger();
  const { currentStep, enableNext, enableSave } = usePanel();
  const [, startLoading] = useRequest();

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [action, setAction] = useState<EventRuleAction>(defaultAction);

  const setActionConfigValue = useCallback(
    (key: string, value: string) => {
      setAction({
        ...action,
        configuration: {
          ...action.configuration,
          [key]: value,
        },
      });
    },
    [action],
  );

  const resetActionConfig = useCallback((type: EventRuleActionType, retained: Record<string, string>) => {
    const defaultConfig = getDefaultAction(type).configuration;
    Object.entries(defaultConfig).forEach(([k, v]) => {
      if (retained[k] === undefined) {
        retained[k] = v;
      }
    });

    setAction({
      type,
      configuration: retained,
    });
  }, []);

  const eventRulePayload = useMemo(() => {
    if ([name, coordinate].includes('') || action === undefined || eventTrigger === undefined) {
      return undefined;
    }

    return {
      name,
      coordinate,
      action,
      trigger: eventTrigger,
    } as EventRule;
  }, [name, coordinate, action, eventTrigger]);

  const reset = useCallback(() => {
    setName(eventRule?.name ?? '');
    setCoordinate(eventRule?.coordinate ?? '');
    setAction(eventRule?.action ?? { ...defaultAction });
    setTriggers(eventRule?.trigger);
  }, [eventRule]);

  const save = useCallback(() => {
    if (eventRulePayload === undefined) {
      return;
    }

    const { name, coordinate, action, trigger } = eventRulePayload;

    startLoading(
      async () => await api.saveEventRule({ name, coordinate }, { action, trigger }, eventRule !== undefined),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [api, closeHandler, eventRulePayload, eventRule]);

  useEffect(() => {
    reset();
  }, [eventRule]);

  /** reactive button enabling */
  useEffect(() => {
    enableSave(eventRulePayload !== undefined);

    switch (currentStep) {
      case 0:
        enableNext(!!coordinate && !!name && coordinate != '' && name !== '');
        break;
    }
  }, [eventRulePayload, name, coordinate, currentStep]);

  const actionForm = useMemo(() => {
    // FIXME: add action config properties to apisdk?
    switch (action?.type) {
      case EventRuleActionType.WEBHOOK:
        return (
          <>
            <StyledTextField
              label={'URL'}
              value={action.configuration.url}
              onChange={(e) => {
                setActionConfigValue('url', e.target.value as string);
              }}
            />
            <StyledCheckbox
              label={'Authentication'}
              value={action.configuration.authEnabled === '1'}
              onChange={(e) => {
                setActionConfigValue('authEnabled', e.target.checked ? '1' : '0');
              }}
            />
            {action.configuration.authEnabled === '1' && (
              <>
                <StyledSelectField
                  native
                  onChange={(e) => {
                    // clear auth type specific config
                    resetActionConfig(action.type, {
                      url: action.configuration.url,
                      authEnabled: '1',
                      authType: e.target.value as string,
                    });
                  }}
                  value={action.configuration.authType}
                  label={'Type'}
                >
                  <option key={0} value={'oauth2-client-credentials'}>
                    {'OAuth 2.0 - Client Credentials'}
                  </option>
                  <option key={1} value={'bearer-token'}>
                    {'Bearer Token'}
                  </option>
                  <option key={2} value={'basic'}>
                    {'Basic'}
                  </option>
                </StyledSelectField>
                {action.configuration.authType === 'oauth2-client-credentials' && (
                  <>
                    <StyledTextField
                      label={'Access Token URL'}
                      value={action.configuration.authUrlAccessToken}
                      onChange={(e) => {
                        setActionConfigValue('authUrlAccessToken', e.target.value as string);
                      }}
                    />

                    <StyledTextField
                      label={'Client ID'}
                      value={action.configuration.authClientId}
                      onChange={(e) => {
                        setActionConfigValue('authClientId', e.target.value as string);
                      }}
                    />

                    <StyledTextField
                      label={'Client Secret'}
                      type={'password'}
                      value={action.configuration.authClientSecret}
                      onChange={(e) => {
                        setActionConfigValue('authClientSecret', e.target.value as string);
                      }}
                    />
                  </>
                )}
                {action.configuration.authType === 'bearer-token' && (
                  <>
                    <StyledTextField
                      type={'password'}
                      label={'Token'}
                      value={action.configuration.authToken}
                      onChange={(e) => {
                        setActionConfigValue('authToken', e.target.value as string);
                      }}
                    />
                    <StyledTextField
                      label={'Prefix'}
                      value={action.configuration.authPrefix}
                      onChange={(e) => {
                        setActionConfigValue('authPrefix', e.target.value as string);
                      }}
                    />
                  </>
                )}
                {action.configuration.authType === 'basic' && (
                  <>
                    <StyledTextField
                      label={'Username'}
                      value={action.configuration.authUsername}
                      onChange={(e) => {
                        setActionConfigValue('authUsername', e.target.value as string);
                      }}
                    />
                    <StyledTextField
                      type={'password'}
                      label={'Password'}
                      value={action.configuration.authPassword}
                      onChange={(e) => {
                        setActionConfigValue('authPassword', e.target.value as string);
                      }}
                    />
                    <StyledCheckbox
                      label={'Use ISO 8859-1'}
                      value={action.configuration.authUseIso8859_1 === '1'}
                      onChange={(e) => {
                        setActionConfigValue('authUseIso8859_1', e.target.checked ? '1' : '0');
                      }}
                    />
                  </>
                )}
              </>
            )}
          </>
        );
      case EventRuleActionType.MQTT:
        return (
          <StyledTextField
            label={'Topic'}
            value={action.configuration.topic}
            onChange={(e) => {
              setActionConfigValue('topic', e.target.value as string);
            }}
          />
        );
      case EventRuleActionType.CLEAR_EVENT_COMPARTMENT:
        return (
          <>
            <StyledTextField
              label={'Clear Value'}
              value={action.configuration.clearValue}
              onChange={(e) => setActionConfigValue('clearValue', e.target.value as string)}
              tooltip={'value to be written into compartment fields'}
            />
            <div>
              <Tooltip title={'Defaults to clearing ALL fields'}>
                <Typography color="textSecondary" variant={'overline'}>
                  Fields
                </Typography>
              </Tooltip>
              <ListForm
                items={
                  action.configuration.fields && action.configuration.fields !== ''
                    ? action.configuration.fields.split(',')
                    : []
                }
                onChange={(items) => setActionConfigValue('fields', items.join(','))}
              />
            </div>
          </>
        );
      case EventRuleActionType.EKANBAN_TRIGGER:
        const selectorName = action?.configuration.compartmentSelectorName ?? '';
        const selectorIndex: CompartmentSelectorIndex | undefined =
          selectorName == ''
            ? undefined
            : {
                name: selectorName,
                coordinate: action!.configuration.compartmentSelectorCoordinate,
              };
        return (
          <>
            <div>
              <CompartmentSelectorPicker
                coordinate={coordinate ?? ''}
                onChange={(selector: CompartmentSelector | undefined) => {
                  if (selector !== undefined) {
                    setActionConfigValue('compartmentSelectorName', selector.name);
                    setActionConfigValue('compartmentSelectorCoordinate', selector.coordinate);
                  } else {
                    setActionConfigValue('compartmentSelectorName', '');
                    setActionConfigValue('compartmentSelectorCoordinate', '');
                  }
                }}
                selected={selectorIndex}
              />
            </div>
            <StyledTextField
              label={'Input JsonPath'}
              value={action.configuration.inputJsonPath}
              onChange={(e) => {
                setActionConfigValue('inputJsonPath', e.target.value as string);
              }}
              tooltip={
                'JsonPath for extracting the Compartment Selector input from the event payload.\n\nExample: $.event.coordinate'
              }
            />
            <StyledTextField
              label={'Trigger Key'}
              value={action.configuration.triggerKey}
              onChange={(e) => {
                setActionConfigValue('triggerKey', e.target.value as string);
              }}
              tooltip={
                'Set a matching key on the ekanban state trigger to only initiate transitions from specific states'
              }
            />
          </>
        );
      default:
        return <></>;
    }
  }, [reset, action, coordinate]);

  const identifierStep = useMemo(() => {
    const isDisabled = eventRule !== undefined;

    return {
      title: 'Event Rule',
      description: <Typography color={'primary'}>{name + (coordinate ? '@' + coordinate : '')}</Typography>,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            type={'text'}
            label={'Name'}
            value={name}
            disabled={isDisabled}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          <CoordinateInput
            value={coordinate}
            disabled={isDisabled}
            onChange={(coordinate) => {
              setCoordinate(coordinate);
            }}
            trailingDelimiter={false}
          />
        </ReactivePanelColumn>
      ),
    };
  }, [eventRule, name, coordinate]);

  const ruleConfigStep = useMemo(() => {
    const actionSelect = (
      <div>
        <StyledSelectField
          native
          onChange={(e) => {
            setAction(getDefaultAction(e.target.value as EventRuleActionType));
          }}
          value={action?.type}
          label={'Type'}
        >
          {Object.keys(EventRuleActionType).map((actionTypeKey: string, index: number) => (
            <option key={index} value={EventRuleActionType[actionTypeKey as never]}>
              {EventRuleActionType[actionTypeKey as never]}
            </option>
          ))}
        </StyledSelectField>
      </div>
    );

    return {
      title: 'Configuration',
      contentCallback: () => (
        <Grid container justifyContent={'center'} spacing={2}>
          <Grid item xs={5}>
            <Stack direction={'column'} alignItems={'stretch'} justifyContent={'center'}>
              <StyledFormHeader label={'Trigger Events'} />
              <EventTriggerForm variant={'list'} coordinate={coordinate!} />
            </Stack>
          </Grid>
          <Grid item>
            <ArrowForward color={'primary'} />
          </Grid>
          <Grid item xs={5}>
            <Stack direction={'column'} spacing={1} alignItems={'stretch'} justifyContent={'center'}>
              <StyledFormHeader label={'Action'} />
              {actionSelect}
              {actionForm}
            </Stack>
          </Grid>
        </Grid>
      ),
    };
  }, [actionForm, eventRule, coordinate, action]);

  const isEditMode = useMemo(() => eventRule !== undefined, [eventRule]);

  return (
    <ContentPanel
      initialStep={isEditMode ? 1 : 0}
      steps={[identifierStep, ruleConfigStep]}
      onCancel={closeHandler}
      onSave={save}
      onReset={reset}
    />
  );
};

export const EventRulePanel = Component;
